import BaseCarousel from "@PegasusCore/pegasus-one-carousel/src/script";

class Carousel extends BaseCarousel {
    /**
     * @var {Boolean}
     */
    smoothAutoplay = false;

    /**
     * @var {Boolean}
     */
    isPaused = false;

    /**
     * @var {string}
     */
    direction = "right";

    /**
     * constructor
     * @var {Boolean}
     * @var {Number}
     * @var {Boolean}
     * @var {String}
     */
    constructor(
        fade,
        overflow,
        autoplay,
        infinite,
        align = "left",
        slidesInView = 1,
        preview = false,
        smoothAutoplay = false,
        direction = "right"
    ) {
        super();
        this.fade = fade;
        this.overflow = overflow;
        this.autoplay = parseInt(autoplay * 1000);
        this.infinite = infinite;
        this.align = align;
        this.slidesInView = Math.ceil(slidesInView);
        this.preview = preview;
        this.smoothAutoplay = smoothAutoplay;
        this.direction = direction;
        
        // Initialize Alpine.js reactive properties
        this.prevButtonDisabled = false;
        this.nextButtonDisabled = false;
        
        // Call updateArrowState after a short delay to ensure DOM is ready
        setTimeout(() => {
            this.updateArrowState();
        }, 200);
    }

    init() {
        super.init();
    
        window.dispatchEvent(new Event("resize"));
    
        if (this.smoothAutoplay && !this.preview) {
            this.initSmoothAutoplay();
        }
    
        requestAnimationFrame(() => {
            this.updateArrowState();
        });
        
        setTimeout(() => {
            this.updateArrowState();
        }, 100);
    }

    initSmoothAutoplay() {
        const tickerSpeed = this.direction === "right" ? 0.5 : -0.5;

        this.carousel.classList.add("dragging");

        const update = () => {
            this.carousel.classList.add("no-transition");
            if (!this.isPaused) {
                if (this.slides.length) {
                    this.currentTranslate += tickerSpeed;
                    this.carousel.style.transform = `translate3d(${this.currentTranslate}px, 0, 0)`;
                    this.currentSlide = this.getClosestSlideSmoothScroll(this.currentTranslate);
                    this.handleInfiniteScroll();
                }
                window.requestAnimationFrame(update);
            }
        };

        update();

        // Pause/resume for mouse hover
        const pause = (e) => {
            if (e.pointerType === "mouse") {
                this.isPaused = true;
            }
        };
        const play = (e) => {
            if (e.pointerType === "mouse") {
                this.isPaused = false;
                window.requestAnimationFrame(update);
            }
        };

        this.carouselWrapper.addEventListener("pointerenter", pause, false);
        this.carouselWrapper.addEventListener("pointerleave", play, false);

        // Touch drag handling
        let isDragging = false;
        let startX = 0;
        let startTranslate = 0;

        this.carouselWrapper.addEventListener("pointerdown", (e) => {
            if (e.pointerType === "touch") {
                isDragging = true;
                this.isPaused = true;
                startX = e.clientX;
                startTranslate = this.currentTranslate;
                this.carouselWrapper.setPointerCapture(e.pointerId);
            }
        });

        this.carouselWrapper.addEventListener("pointermove", (e) => {
            if (isDragging && e.pointerType === "touch") {
                const deltaX = e.clientX - startX;
                this.currentTranslate = startTranslate + deltaX;
                this.carousel.style.transform = `translate3d(${this.currentTranslate}px, 0, 0)`;
            }
        });

        const resumeAutoplay = (e) => {
            if (isDragging && e.pointerType === "touch") {
                isDragging = false;
                this.carouselWrapper.releasePointerCapture(e.pointerId);

                // Ensure currentTranslate syncs with visual transform
                const matrix = window.getComputedStyle(this.carousel).transform;
                if (matrix && matrix !== "none") {
                    const translateX = new DOMMatrix(matrix).m41;
                    this.currentTranslate = translateX;
                }

                this.isPaused = false;
                window.requestAnimationFrame(update);
            }
        };

        this.carouselWrapper.addEventListener("pointerup", resumeAutoplay, false);
        this.carouselWrapper.addEventListener("pointercancel", resumeAutoplay, false);

        // Prevent long-press context menu on mobile
        this.carouselWrapper.addEventListener("contextmenu", (e) => e.preventDefault(), false);
    }

    /**
     * Determine the index of the slide whose left edge aligns with the left edge of the slider
     * @param {Number} currentTranslate - The current translation position of the slider
     * @return {Number} The index of the aligned slide, or null if no alignment
     */
    getClosestSlideSmoothScroll(currentTranslate) {
        let alignedIndex = null;

        this.slides.forEach((slide, index) => {
            // Calculate the slide's position relative to the carousel wrapper
            const slideLeftEdge = slide.offsetLeft - this.carouselWrapper.offsetLeft;
    
            // Check if the slide's left edge aligns perfectly with the carousel edge
            if (Math.round(slideLeftEdge + currentTranslate) === 0) {
                alignedIndex = index;
            }
        });
    
        return alignedIndex;
    }

    /**
     * Updates arrow opacity based on the current slide index
     */
    updateArrowState() {
        const prevButton = document.querySelector(".Navigation__Arrows button:first-child");
        const nextButton = document.querySelector(".Navigation__Arrows button:last-child");
    
        if (prevButton && nextButton) {
            // Ensure currentSlide is within valid bounds
            let currentIndex = this.currentSlide ?? 0;
            if (currentIndex < 0) currentIndex = 0;
            if (currentIndex >= this.slides.length) currentIndex = this.slides.length - 1;
            
            this.prevButtonDisabled = currentIndex === 0;
            this.nextButtonDisabled = currentIndex === this.slides.length - 1;
            
            prevButton.disabled = currentIndex === 0;
            nextButton.disabled = currentIndex === this.slides.length - 1;
        }
    }
    

    /**
     * Override nextSlide to update arrow state properly
     * @param {Number} slidesToScroll
     */
    nextSlide(slidesToScroll = 1) {
        // Check if we're at the last slide
        if (this.currentSlide >= this.slides.length - 1) {
            return;
        }
        
        super.nextSlide(slidesToScroll);
        setTimeout(() => this.updateArrowState(), 50);
    }

    /**
     * Override previousSlide to update arrow state properly
     * @param {Number} slidesToScroll
     */
    previousSlide(slidesToScroll = 1) {
        // Check if we're at the first slide
        if (this.currentSlide <= 0) {
            return;
        }
        
        super.previousSlide(slidesToScroll);
        setTimeout(() => this.updateArrowState(), 50);
    }
}

document.addEventListener("alpine:init", () => {
    Alpine.data(
        "carousel",
        (
            fade,
            overflow,
            autoplay,
            infinite,
            align,
            slidesInView,
            preview,
            smoothAutoplay,
            direction
        ) =>
            new Carousel(
                fade,
                overflow,
                autoplay,
                infinite,
                align,
                slidesInView,
                preview,
                smoothAutoplay,
                direction
            )
    );
});
